/**
 * Errores de la aplicación.
 *
 * Cada error lleva un mensaje ya redactado para el usuario. El detalle técnico
 * queda en `causa` y sólo se registra en consola: un mensaje de error nunca
 * debe filtrar nombres de tablas, consultas ni respuestas del servidor.
 */

export type CodigoError =
  | 'CREDENCIALES_INVALIDAS'
  | 'USUARIO_BLOQUEADO'
  | 'USUARIO_INACTIVO'
  | 'PERFIL_NO_ENCONTRADO'
  | 'EMAIL_NO_CONFIRMADO'
  | 'SESION_EXPIRADA'
  | 'SIN_PERMISO'
  | 'BACKEND_INCOMPATIBLE'
  | 'SIN_CONEXION'
  | 'DATOS_INVALIDOS'
  | 'NO_ENCONTRADO'
  | 'CONFLICTO'
  | 'DESCONOCIDO';

export class ErrorApp extends Error {
  constructor(
    readonly codigo: CodigoError,
    /** Mensaje redactado para mostrar tal cual al usuario. */
    override readonly message: string,
    readonly causa?: unknown,
  ) {
    super(message);
    this.name = 'ErrorApp';
  }

  /** `true` si reintentar la misma acción tiene sentido. */
  get esReintentable(): boolean {
    return this.codigo === 'SIN_CONEXION' || this.codigo === 'DESCONOCIDO';
  }

  /** `true` si el usuario debe volver a iniciar sesión. */
  get exigeReautenticar(): boolean {
    return this.codigo === 'SESION_EXPIRADA';
  }
}

/** Mensajes por defecto de cada código. */
const MENSAJES: Readonly<Record<CodigoError, string>> = {
  CREDENCIALES_INVALIDAS: 'Correo o contraseña incorrectos.',
  USUARIO_BLOQUEADO: 'Tu cuenta está bloqueada. Contacta al administrador.',
  USUARIO_INACTIVO: 'Tu cuenta está inactiva. Contacta al administrador.',
  PERFIL_NO_ENCONTRADO: 'Tu usuario no tiene un perfil asignado. Contacta al administrador.',
  EMAIL_NO_CONFIRMADO: 'Debes confirmar tu correo antes de iniciar sesión.',
  SESION_EXPIRADA: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  SIN_PERMISO: 'No tienes permiso para realizar esta acción.',
  BACKEND_INCOMPATIBLE:
    'Esta versión de la aplicación no es compatible con el servidor. Recarga la página; si persiste, avisa al administrador.',
  SIN_CONEXION: 'No se pudo contactar al servidor. Revisa tu conexión e inténtalo de nuevo.',
  DATOS_INVALIDOS: 'Los datos ingresados no son válidos. Revisa el formulario.',
  NO_ENCONTRADO: 'No se encontró lo que buscabas. Puede que otra persona lo haya eliminado.',
  CONFLICTO: 'Otra persona modificó este registro. Recarga para ver los cambios más recientes.',
  DESCONOCIDO: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
};

export function crearError(codigo: CodigoError, causa?: unknown, mensaje?: string): ErrorApp {
  return new ErrorApp(codigo, mensaje ?? MENSAJES[codigo], causa);
}

/**
 * Traduce un error de PostgREST o de la red a un `ErrorApp`.
 *
 * El detalle original se conserva en `causa` para la consola, pero lo que ve
 * el usuario es siempre uno de los mensajes de arriba.
 */
export function traducirErrorSupabase(error: unknown): ErrorApp {
  if (error instanceof ErrorApp) return error;

  // El usuario verá un mensaje redactado, así que el detalle técnico tiene que
  // quedar registrado aquí o se pierde y no hay forma de diagnosticar nada.
  registrarError('supabase', error);

  const posible = error as { code?: string; status?: number; message?: string } | null;
  const codigoPg = posible?.code;
  const estado = posible?.status;
  const mensaje = posible?.message ?? '';

  if (mensaje.includes('Failed to fetch') || mensaje.includes('NetworkError')) {
    return crearError('SIN_CONEXION', error);
  }

  // 42501 = insufficient_privilege; PGRST301 = JWT inválido o expirado.
  if (codigoPg === '42501' || estado === 403) return crearError('SIN_PERMISO', error);
  if (codigoPg === 'PGRST301' || estado === 401) return crearError('SESION_EXPIRADA', error);

  // PGRST116 = la consulta con `.single()` no devolvió filas.
  if (codigoPg === 'PGRST116' || estado === 404) return crearError('NO_ENCONTRADO', error);

  // 23505 = violación de unicidad; 23503 = clave foránea.
  if (codigoPg === '23505' || codigoPg === '23503' || estado === 409) {
    return crearError('CONFLICTO', error);
  }

  if (codigoPg?.startsWith('22') || codigoPg === '23514' || estado === 400) {
    return crearError('DATOS_INVALIDOS', error);
  }

  return crearError('DESCONOCIDO', error);
}

/**
 * Registra el detalle técnico de un error.
 * En producción esto es lo único que lo hace visible: la pantalla sólo muestra
 * el mensaje redactado.
 */
export function registrarError(contexto: string, error: unknown): void {
  console.error(`[${contexto}]`, error);
}
